import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Globe, Settings, Users, Star, FileText, Search as SearchIcon, MapPin,
  Plus, Edit2, Trash2, Eye, RefreshCw, ChevronDown, BarChart3, MessageCircle
} from 'lucide-react';
import api from '@/utils/api';

const tabs = [
  { id: 'settings', label: 'Site Settings', icon: Settings },
  { id: 'seo', label: 'SEO Pages', icon: SearchIcon },
  { id: 'blog', label: 'Blog Posts', icon: FileText },
  { id: 'testimonials', label: 'Testimonials', icon: Star },
  { id: 'enquiries', label: 'Enquiries', icon: MessageCircle },
];

export default function WebsiteManager({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(false);

  // Settings state
  const [siteSettings, setSiteSettings] = useState({
    company_name: 'Ganesa Homestate Developers', tagline: '', description: '', mission: '', vision: '',
    phone: '', email: '', address: '', website: '', whatsapp: '',
    google_maps_url: '', google_maps_embed: '',
    social: { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '' },
    hero_title: '', hero_subtitle: '', about_content: '',
    team_members: [], stats: { projects: '50+', customers: '1000+', years: '10+', cities: '5+' },
    services: [],
    service_areas: [],
    footer_text: '', footer_disclaimer: '',
    gallery_images: [],
    meta_title: '', meta_description: '', meta_keywords: '',
    google_analytics_id: '', google_search_console: '',
    popup_enabled: false, popup_image_url: '',
  });

  // SEO state
  const [seoPages, setSeoPages] = useState([]);
  const [seoDialog, setSeoDialog] = useState(false);
  const [seoForm, setSeoForm] = useState({ title: '', slug: '', meta_title: '', meta_description: '', h1: '', content: '', keywords: '', location: '', property_type: '', faq: [] });
  const [editSeoId, setEditSeoId] = useState(null);

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogDialog, setBlogDialog] = useState(false);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', excerpt: '', meta_title: '', meta_description: '', keywords: '', category: 'General', status: 'published' });
  const [editBlogId, setEditBlogId] = useState(null);

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialDialog, setTestimonialDialog] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ customer_name: '', location: '', rating: 5, review: '', property_type: '' });
  const [editTestimonialId, setEditTestimonialId] = useState(null);

  // Enquiries state
  const [enquiries, setEnquiries] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [s, seo, b, t, e] = await Promise.all([
        api.get('/website-settings').catch(() => ({ data: {} })),
        api.get('/seo-pages').catch(() => ({ data: [] })),
        api.get('/blog-posts').catch(() => ({ data: [] })),
        api.get('/testimonials').catch(() => ({ data: [] })),
        api.get('/enquiries').catch(() => ({ data: [] })),
      ]);
      if (s.data && Object.keys(s.data).length) setSiteSettings(prev => ({ ...prev, ...s.data }));
      setSeoPages(seo.data || []);
      setBlogs(b.data || []);
      setTestimonials(t.data || []);
      setEnquiries(e.data || []);
    } catch (err) { console.error(err); }
  };

  // === SETTINGS ===
  const saveSettings = async () => {
    setLoading(true);
    try {
      await api.put('/website-settings', siteSettings);
      toast.success('Website settings saved!');
    } catch { toast.error('Failed to save settings'); }
    finally { setLoading(false); }
  };

  // === SEO ===
  const autoGenerateSeo = async () => {
    setLoading(true);
    try {
      const res = await api.post('/seo-pages/auto-generate');
      toast.success(`${res.data.total_created} SEO pages generated!`);
      fetchAll();
    } catch { toast.error('Failed to generate SEO pages'); }
    finally { setLoading(false); }
  };

  const saveSeo = async () => {
    setLoading(true);
    try {
      if (editSeoId) {
        await api.put(`/seo-pages/${editSeoId}`, seoForm);
      } else {
        await api.post('/seo-pages', seoForm);
      }
      toast.success('SEO page saved!');
      setSeoDialog(false);
      setSeoForm({ title: '', slug: '', meta_title: '', meta_description: '', h1: '', content: '', keywords: '', location: '', property_type: '', faq: [] });
      setEditSeoId(null);
      fetchAll();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const deleteSeo = async (id) => {
    if (!window.confirm('Delete this SEO page?')) return;
    await api.delete(`/seo-pages/${id}`);
    toast.success('Deleted');
    fetchAll();
  };

  // === BLOG ===
  const saveBlog = async () => {
    setLoading(true);
    try {
      if (editBlogId) {
        await api.put(`/blog-posts/${editBlogId}`, blogForm);
      } else {
        await api.post('/blog-posts', blogForm);
      }
      toast.success('Blog post saved!');
      setBlogDialog(false);
      setBlogForm({ title: '', content: '', excerpt: '', meta_title: '', meta_description: '', keywords: '', category: 'General', status: 'published' });
      setEditBlogId(null);
      fetchAll();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    await api.delete(`/blog-posts/${id}`);
    toast.success('Deleted');
    fetchAll();
  };

  // === TESTIMONIALS ===
  const saveTestimonial = async () => {
    setLoading(true);
    try {
      if (editTestimonialId) {
        await api.put(`/testimonials/${editTestimonialId}`, testimonialForm);
      } else {
        await api.post('/testimonials', testimonialForm);
      }
      toast.success('Testimonial saved!');
      setTestimonialDialog(false);
      setTestimonialForm({ customer_name: '', location: '', rating: 5, review: '', property_type: '' });
      setEditTestimonialId(null);
      fetchAll();
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    await api.delete(`/testimonials/${id}`);
    toast.success('Deleted');
    fetchAll();
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="website">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="website-manager-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Globe size={24} className="text-emerald-600" /> Website Manager</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your public website content, SEO pages, and blog</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 bg-white border rounded-xl px-4 py-2.5 shadow-sm">
              <span className="text-sm font-medium text-slate-700">Website</span>
              <button
                data-testid="website-toggle"
                onClick={async () => {
                  const newVal = !siteSettings.website_enabled;
                  setSiteSettings({ ...siteSettings, website_enabled: newVal });
                  try {
                    await api.put('/website-settings', { ...siteSettings, website_enabled: newVal });
                    toast.success(newVal ? 'Website Enabled! Public pages are now live.' : 'Website Disabled! Visitors will see login page.');
                  } catch { toast.error('Failed to update'); }
                }}
                className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${siteSettings.website_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${siteSettings.website_enabled ? 'translate-x-6' : ''}`} />
              </button>
              <span className={`text-xs font-bold ${siteSettings.website_enabled ? 'text-emerald-600' : 'text-red-500'}`}>
                {siteSettings.website_enabled ? 'LIVE' : 'OFF'}
              </span>
            </div>
            <a href="/" target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-emerald-700">
              <Eye size={16} /> Preview
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 overflow-x-auto pb-2 border-b">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-emerald-50 text-emerald-700 border-b-2 border-emerald-600' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <t.icon size={16} /> {t.label}
              {t.id === 'seo' && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{seoPages.length}</span>}
              {t.id === 'enquiries' && enquiries.length > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{enquiries.filter(e => e.status === 'new').length}</span>}
            </button>
          ))}
        </div>

        {/* === SETTINGS TAB === */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Popup Banner */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-200 bg-amber-50/30">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Eye size={18} className="text-amber-600" /> Popup Banner (Site Open Hone Pe)</h2>
              <p className="text-xs text-slate-400 mb-4">Jab koi visitor website kholega toh yeh image popup me dikhegi. Close button automatically aa jayega.</p>
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium text-slate-700">Popup Status:</span>
                <button
                  data-testid="popup-toggle"
                  onClick={() => setSiteSettings({...siteSettings, popup_enabled: !siteSettings.popup_enabled})}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${siteSettings.popup_enabled ? 'bg-amber-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${siteSettings.popup_enabled ? 'translate-x-6' : ''}`} />
                </button>
                <span className={`text-xs font-bold ${siteSettings.popup_enabled ? 'text-amber-600' : 'text-slate-400'}`}>
                  {siteSettings.popup_enabled ? 'ON - Popup Active' : 'OFF'}
                </span>
              </div>
              <div className="space-y-3">
                <div>
                  <Label>Popup Image URL</Label>
                  <Input value={siteSettings.popup_image_url || ''} onChange={e => setSiteSettings({...siteSettings, popup_image_url: e.target.value})} placeholder="https://... (image URL paste karein)" />
                </div>
                {siteSettings.popup_image_url && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-400 mb-2">Preview:</p>
                    <img src={siteSettings.popup_image_url} alt="Popup Preview" className="max-w-xs rounded-lg border shadow-sm" onError={e => e.target.style.display='none'} />
                  </div>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Users size={18} className="text-emerald-600" /> Company Information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Company Name</Label><Input value={siteSettings.company_name} onChange={e => setSiteSettings({...siteSettings, company_name: e.target.value})} /></div>
                <div><Label>Tagline</Label><Input value={siteSettings.tagline} onChange={e => setSiteSettings({...siteSettings, tagline: e.target.value})} placeholder="Your Trusted Property Partner" /></div>
                <div className="sm:col-span-2"><Label>Description</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={siteSettings.description} onChange={e => setSiteSettings({...siteSettings, description: e.target.value})} /></div>
                <div className="sm:col-span-2"><Label>About Us Content</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={4} value={siteSettings.about_content} onChange={e => setSiteSettings({...siteSettings, about_content: e.target.value})} /></div>
                <div><Label>Mission</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={siteSettings.mission} onChange={e => setSiteSettings({...siteSettings, mission: e.target.value})} /></div>
                <div><Label>Vision</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={siteSettings.vision} onChange={e => setSiteSettings({...siteSettings, vision: e.target.value})} /></div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><MapPin size={18} className="text-emerald-600" /> Contact Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Phone</Label><Input value={siteSettings.phone} onChange={e => setSiteSettings({...siteSettings, phone: e.target.value})} /></div>
                <div><Label>Email</Label><Input value={siteSettings.email} onChange={e => setSiteSettings({...siteSettings, email: e.target.value})} /></div>
                <div><Label>WhatsApp Number</Label><Input value={siteSettings.whatsapp} onChange={e => setSiteSettings({...siteSettings, whatsapp: e.target.value})} /></div>
                <div><Label>Website URL</Label><Input value={siteSettings.website} onChange={e => setSiteSettings({...siteSettings, website: e.target.value})} /></div>
                <div className="sm:col-span-2"><Label>Office Address</Label><Input value={siteSettings.address} onChange={e => setSiteSettings({...siteSettings, address: e.target.value})} /></div>
                <div className="sm:col-span-2"><Label>Google Maps Link</Label><Input value={siteSettings.google_maps_url} onChange={e => setSiteSettings({...siteSettings, google_maps_url: e.target.value})} placeholder="https://maps.google.com/..." /></div>
                <div className="sm:col-span-2"><Label>Google Maps Embed Code (iframe)</Label><Input value={siteSettings.google_maps_embed} onChange={e => setSiteSettings({...siteSettings, google_maps_embed: e.target.value})} placeholder='<iframe src="https://www.google.com/maps/embed?..." />' /></div>
              </div>
            </div>

            {/* Hero Section */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4">Hero Section</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Hero Title</Label><Input value={siteSettings.hero_title} onChange={e => setSiteSettings({...siteSettings, hero_title: e.target.value})} placeholder="Premium Properties for Your Dream Life" /></div>
                <div><Label>Hero Subtitle</Label><Input value={siteSettings.hero_subtitle} onChange={e => setSiteSettings({...siteSettings, hero_subtitle: e.target.value})} /></div>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4">Stats (Shown on Homepage)</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div><Label>Projects</Label><Input value={siteSettings.stats?.projects || ''} onChange={e => setSiteSettings({...siteSettings, stats: {...siteSettings.stats, projects: e.target.value}})} /></div>
                <div><Label>Customers</Label><Input value={siteSettings.stats?.customers || ''} onChange={e => setSiteSettings({...siteSettings, stats: {...siteSettings.stats, customers: e.target.value}})} /></div>
                <div><Label>Years</Label><Input value={siteSettings.stats?.years || ''} onChange={e => setSiteSettings({...siteSettings, stats: {...siteSettings.stats, years: e.target.value}})} /></div>
                <div><Label>Cities</Label><Input value={siteSettings.stats?.cities || ''} onChange={e => setSiteSettings({...siteSettings, stats: {...siteSettings.stats, cities: e.target.value}})} /></div>
              </div>
            </div>

            {/* Services */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><BarChart3 size={18} className="text-emerald-600" /> Services</h2>
              <p className="text-xs text-slate-400 mb-4">Yeh services About page aur Homepage pe show hongi</p>
              {(siteSettings.services || []).map((s, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <Input placeholder="Service name" value={s.title || ''} onChange={e => { const svcs = [...(siteSettings.services || [])]; svcs[i] = {...svcs[i], title: e.target.value}; setSiteSettings({...siteSettings, services: svcs}); }} className="flex-1" />
                  <Input placeholder="Short description" value={s.description || ''} onChange={e => { const svcs = [...(siteSettings.services || [])]; svcs[i] = {...svcs[i], description: e.target.value}; setSiteSettings({...siteSettings, services: svcs}); }} className="flex-[2]" />
                  <button onClick={() => { const svcs = (siteSettings.services || []).filter((_, idx) => idx !== i); setSiteSettings({...siteSettings, services: svcs}); }} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setSiteSettings({...siteSettings, services: [...(siteSettings.services || []), {title: '', description: ''}]})} className="gap-1 mt-1"><Plus size={14} /> Add Service</Button>
            </div>

            {/* Service Areas */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><MapPin size={18} className="text-emerald-600" /> Service Areas / Locations</h2>
              <p className="text-xs text-slate-400 mb-4">SEO ke liye important - jahan property hai unke names</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {(siteSettings.service_areas || []).map((a, i) => (
                  <span key={i} className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full text-sm">
                    {a}
                    <button onClick={() => { const areas = (siteSettings.service_areas || []).filter((_, idx) => idx !== i); setSiteSettings({...siteSettings, service_areas: areas}); }} className="ml-1 text-emerald-400 hover:text-red-500">&times;</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input id="new-area-input" placeholder="e.g. Khatushyam, Jaipur, Sikar" onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    setSiteSettings({...siteSettings, service_areas: [...(siteSettings.service_areas || []), e.target.value.trim()]});
                    e.target.value = '';
                  }
                }} />
                <Button size="sm" variant="outline" onClick={() => {
                  const inp = document.getElementById('new-area-input');
                  if (inp?.value?.trim()) {
                    setSiteSettings({...siteSettings, service_areas: [...(siteSettings.service_areas || []), inp.value.trim()]});
                    inp.value = '';
                  }
                }}><Plus size={14} /></Button>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4">Social Media Links</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Facebook URL</Label><Input value={siteSettings.social?.facebook || ''} onChange={e => setSiteSettings({...siteSettings, social: {...siteSettings.social, facebook: e.target.value}})} /></div>
                <div><Label>Instagram URL</Label><Input value={siteSettings.social?.instagram || ''} onChange={e => setSiteSettings({...siteSettings, social: {...siteSettings.social, instagram: e.target.value}})} /></div>
                <div><Label>YouTube URL</Label><Input value={siteSettings.social?.youtube || ''} onChange={e => setSiteSettings({...siteSettings, social: {...siteSettings.social, youtube: e.target.value}})} /></div>
                <div><Label>Twitter URL</Label><Input value={siteSettings.social?.twitter || ''} onChange={e => setSiteSettings({...siteSettings, social: {...siteSettings.social, twitter: e.target.value}})} /></div>
                <div><Label>LinkedIn URL</Label><Input value={siteSettings.social?.linkedin || ''} onChange={e => setSiteSettings({...siteSettings, social: {...siteSettings.social, linkedin: e.target.value}})} /></div>
              </div>
            </div>

            {/* SEO & Analytics */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><SearchIcon size={18} className="text-emerald-600" /> SEO & Analytics</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Default Meta Title</Label><Input value={siteSettings.meta_title} onChange={e => setSiteSettings({...siteSettings, meta_title: e.target.value})} placeholder="Ganesa Homestate | Premium Plots & Properties" /></div>
                <div><Label>Meta Keywords (comma separated)</Label><Input value={siteSettings.meta_keywords} onChange={e => setSiteSettings({...siteSettings, meta_keywords: e.target.value})} placeholder="plots in rajasthan, property dealer, ganesa homestate" /></div>
                <div className="sm:col-span-2"><Label>Meta Description</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={siteSettings.meta_description} onChange={e => setSiteSettings({...siteSettings, meta_description: e.target.value})} placeholder="Ganesa Homestate Developers - Premium plots, flats, villas..." /></div>
                <div><Label>Google Analytics ID</Label><Input value={siteSettings.google_analytics_id} onChange={e => setSiteSettings({...siteSettings, google_analytics_id: e.target.value})} placeholder="G-XXXXXXXXXX" /></div>
                <div><Label>Google Search Console Verification</Label><Input value={siteSettings.google_search_console} onChange={e => setSiteSettings({...siteSettings, google_search_console: e.target.value})} placeholder="Verification code" /></div>
              </div>
            </div>

            {/* Gallery Images */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2"><Eye size={18} className="text-emerald-600" /> Gallery Images</h2>
              <p className="text-xs text-slate-400 mb-4">Image URLs add karo - yeh Gallery page pe show hongi</p>
              {(siteSettings.gallery_images || []).map((img, i) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <Input placeholder="Image URL (https://...)" value={img.url || ''} onChange={e => { const imgs = [...(siteSettings.gallery_images || [])]; imgs[i] = {...imgs[i], url: e.target.value}; setSiteSettings({...siteSettings, gallery_images: imgs}); }} className="flex-[2]" />
                  <Input placeholder="Caption (optional)" value={img.caption || ''} onChange={e => { const imgs = [...(siteSettings.gallery_images || [])]; imgs[i] = {...imgs[i], caption: e.target.value}; setSiteSettings({...siteSettings, gallery_images: imgs}); }} className="flex-1" />
                  {img.url && <img src={img.url} alt="" className="w-10 h-10 rounded object-cover border" onError={e => e.target.style.display='none'} />}
                  <button onClick={() => { const imgs = (siteSettings.gallery_images || []).filter((_, idx) => idx !== i); setSiteSettings({...siteSettings, gallery_images: imgs}); }} className="p-2 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setSiteSettings({...siteSettings, gallery_images: [...(siteSettings.gallery_images || []), {url: '', caption: ''}]})} className="gap-1 mt-1" data-testid="add-gallery-image-btn"><Plus size={14} /> Add Image</Button>
            </div>

            {/* Footer */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="font-bold text-lg mb-4">Footer Content</h2>
              <div className="space-y-4">
                <div><Label>Footer Text</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={siteSettings.footer_text} onChange={e => setSiteSettings({...siteSettings, footer_text: e.target.value})} placeholder="Your trusted partner for premium real estate..." /></div>
                <div><Label>Disclaimer / Legal Text</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={siteSettings.footer_disclaimer} onChange={e => setSiteSettings({...siteSettings, footer_disclaimer: e.target.value})} placeholder="All prices and availability subject to change..." /></div>
              </div>
            </div>

            <Button onClick={saveSettings} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8" data-testid="save-settings-btn">
              {loading ? 'Saving...' : 'Save All Settings'}
            </Button>
          </div>
        )}

        {/* === SEO PAGES TAB === */}
        {activeTab === 'seo' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <p className="text-sm text-slate-500">{seoPages.length} SEO pages active for organic search visibility</p>
              <div className="flex gap-2">
                <Button onClick={autoGenerateSeo} disabled={loading} variant="outline" className="gap-2" data-testid="auto-generate-seo">
                  <RefreshCw size={16} /> Auto-Generate
                </Button>
                <Button onClick={() => { setEditSeoId(null); setSeoForm({ title: '', slug: '', meta_title: '', meta_description: '', h1: '', content: '', keywords: '', location: '', property_type: '', faq: [] }); setSeoDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                  <Plus size={16} /> Add SEO Page
                </Button>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase">
                  <th className="py-3 px-4">Title</th><th className="py-3 px-4">Location</th><th className="py-3 px-4">Type</th><th className="py-3 px-4">Actions</th>
                </tr></thead>
                <tbody>
                  {seoPages.slice(0, 50).map(sp => (
                    <tr key={sp.id} className="border-t hover:bg-slate-50">
                      <td className="py-3 px-4"><div className="font-medium text-sm">{sp.title}</div><div className="text-xs text-slate-400">/{sp.slug}</div></td>
                      <td className="py-3 px-4 text-sm">{sp.location}</td>
                      <td className="py-3 px-4 text-sm">{sp.property_type}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <button onClick={() => { setEditSeoId(sp.id); setSeoForm(sp); setSeoDialog(true); }} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600"><Edit2 size={14} /></button>
                          <a href={`/location/${sp.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-blue-50 rounded text-blue-600"><Eye size={14} /></a>
                          <button onClick={() => deleteSeo(sp.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {seoPages.length > 50 && <div className="p-3 text-center text-xs text-slate-400">Showing 50 of {seoPages.length} pages</div>}
            </div>

            {/* SEO Dialog */}
            <Dialog open={seoDialog} onOpenChange={setSeoDialog}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editSeoId ? 'Edit' : 'Add'} SEO Page</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Title *</Label><Input value={seoForm.title} onChange={e => setSeoForm({...seoForm, title: e.target.value})} placeholder="Plots in Khatushyam" /></div>
                    <div><Label>Slug</Label><Input value={seoForm.slug} onChange={e => setSeoForm({...seoForm, slug: e.target.value})} placeholder="plots-in-khatushyam" /></div>
                    <div><Label>Location</Label><Input value={seoForm.location} onChange={e => setSeoForm({...seoForm, location: e.target.value})} placeholder="Khatushyam" /></div>
                    <div><Label>Property Type</Label><Input value={seoForm.property_type} onChange={e => setSeoForm({...seoForm, property_type: e.target.value})} placeholder="Plots" /></div>
                  </div>
                  <div><Label>Meta Title (SEO)</Label><Input value={seoForm.meta_title} onChange={e => setSeoForm({...seoForm, meta_title: e.target.value})} /></div>
                  <div><Label>Meta Description (SEO)</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={seoForm.meta_description} onChange={e => setSeoForm({...seoForm, meta_description: e.target.value})} /></div>
                  <div><Label>H1 Heading</Label><Input value={seoForm.h1} onChange={e => setSeoForm({...seoForm, h1: e.target.value})} /></div>
                  <div><Label>Keywords (comma separated)</Label><Input value={seoForm.keywords} onChange={e => setSeoForm({...seoForm, keywords: e.target.value})} /></div>
                  <div><Label>Content (HTML)</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm font-mono" rows={8} value={seoForm.content} onChange={e => setSeoForm({...seoForm, content: e.target.value})} /></div>
                  <Button onClick={saveSeo} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">{loading ? 'Saving...' : 'Save SEO Page'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* === BLOG TAB === */}
        {activeTab === 'blog' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">{blogs.length} blog posts</p>
              <Button onClick={() => { setEditBlogId(null); setBlogForm({ title: '', content: '', excerpt: '', meta_title: '', meta_description: '', keywords: '', category: 'General', status: 'published' }); setBlogDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                <Plus size={16} /> Add Blog Post
              </Button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase">
                  <th className="py-3 px-4">Title</th><th className="py-3 px-4">Category</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Date</th><th className="py-3 px-4">Actions</th>
                </tr></thead>
                <tbody>
                  {blogs.map(b => (
                    <tr key={b.id} className="border-t hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-sm">{b.title}</td>
                      <td className="py-3 px-4 text-sm">{b.category}</td>
                      <td className="py-3 px-4"><span className={`text-xs font-bold px-2 py-1 rounded-full ${b.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{b.status}</span></td>
                      <td className="py-3 px-4 text-sm text-slate-500">{new Date(b.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <button onClick={() => { setEditBlogId(b.id); setBlogForm(b); setBlogDialog(true); }} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600"><Edit2 size={14} /></button>
                          <a href={`/blog/${b.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-blue-50 rounded text-blue-600"><Eye size={14} /></a>
                          <button onClick={() => deleteBlog(b.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {blogs.length === 0 && <div className="p-8 text-center text-slate-400">No blog posts yet</div>}
            </div>

            <Dialog open={blogDialog} onOpenChange={setBlogDialog}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{editBlogId ? 'Edit' : 'Add'} Blog Post</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label>Title *</Label><Input value={blogForm.title} onChange={e => setBlogForm({...blogForm, title: e.target.value})} /></div>
                    <div><Label>Category</Label>
                      <select className="w-full border rounded-lg px-3 py-2 text-sm" value={blogForm.category} onChange={e => setBlogForm({...blogForm, category: e.target.value})}>
                        {['General', 'Investment Tips', 'Market Update', 'Property Guide', 'News', 'Legal'].map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div><Label>Excerpt / Summary</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={blogForm.excerpt} onChange={e => setBlogForm({...blogForm, excerpt: e.target.value})} /></div>
                  <div><Label>Content (HTML)</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm font-mono" rows={10} value={blogForm.content} onChange={e => setBlogForm({...blogForm, content: e.target.value})} /></div>
                  <div><Label>Meta Title (SEO)</Label><Input value={blogForm.meta_title} onChange={e => setBlogForm({...blogForm, meta_title: e.target.value})} /></div>
                  <div><Label>Meta Description (SEO)</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} value={blogForm.meta_description} onChange={e => setBlogForm({...blogForm, meta_description: e.target.value})} /></div>
                  <div><Label>Keywords</Label><Input value={blogForm.keywords} onChange={e => setBlogForm({...blogForm, keywords: e.target.value})} /></div>
                  <div><Label>Status</Label>
                    <select className="w-full border rounded-lg px-3 py-2 text-sm" value={blogForm.status} onChange={e => setBlogForm({...blogForm, status: e.target.value})}>
                      <option value="published">Published</option><option value="draft">Draft</option>
                    </select>
                  </div>
                  <Button onClick={saveBlog} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">{loading ? 'Saving...' : 'Save Blog Post'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* === TESTIMONIALS TAB === */}
        {activeTab === 'testimonials' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">{testimonials.length} testimonials</p>
              <Button onClick={() => { setEditTestimonialId(null); setTestimonialForm({ customer_name: '', location: '', rating: 5, review: '', property_type: '' }); setTestimonialDialog(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                <Plus size={16} /> Add Testimonial
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map(t => (
                <div key={t.id} className="bg-white rounded-xl p-5 shadow-sm border">
                  <div className="flex gap-1 mb-3">{[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} className="fill-amber-400 text-amber-400" />)}</div>
                  <p className="text-sm text-slate-600 italic mb-3 line-clamp-3">"{t.review}"</p>
                  <div className="flex justify-between items-center">
                    <div><div className="font-semibold text-sm">{t.customer_name}</div><div className="text-xs text-slate-400">{t.location}</div></div>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditTestimonialId(t.id); setTestimonialForm(t); setTestimonialDialog(true); }} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-600"><Edit2 size={14} /></button>
                      <button onClick={() => deleteTestimonial(t.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {testimonials.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No testimonials yet</div>}
            </div>

            <Dialog open={testimonialDialog} onOpenChange={setTestimonialDialog}>
              <DialogContent>
                <DialogHeader><DialogTitle>{editTestimonialId ? 'Edit' : 'Add'} Testimonial</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Customer Name *</Label><Input value={testimonialForm.customer_name} onChange={e => setTestimonialForm({...testimonialForm, customer_name: e.target.value})} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Location</Label><Input value={testimonialForm.location} onChange={e => setTestimonialForm({...testimonialForm, location: e.target.value})} /></div>
                    <div><Label>Rating (1-5)</Label><Input type="number" min="1" max="5" value={testimonialForm.rating} onChange={e => setTestimonialForm({...testimonialForm, rating: parseInt(e.target.value)})} /></div>
                  </div>
                  <div><Label>Property Type</Label><Input value={testimonialForm.property_type} onChange={e => setTestimonialForm({...testimonialForm, property_type: e.target.value})} placeholder="e.g., Plot, Flat" /></div>
                  <div><Label>Review *</Label><textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={4} value={testimonialForm.review} onChange={e => setTestimonialForm({...testimonialForm, review: e.target.value})} /></div>
                  <Button onClick={saveTestimonial} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700">{loading ? 'Saving...' : 'Save Testimonial'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* === ENQUIRIES TAB === */}
        {activeTab === 'enquiries' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">{enquiries.length} enquiries ({enquiries.filter(e => e.status === 'new').length} new)</p>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase">
                  <th className="py-3 px-4">Name</th><th className="py-3 px-4">Phone</th><th className="py-3 px-4">Interest</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Date</th>
                </tr></thead>
                <tbody>
                  {enquiries.map(e => (
                    <tr key={e.id} className="border-t hover:bg-slate-50">
                      <td className="py-3 px-4"><div className="font-medium text-sm">{e.name}</div>{e.email && <div className="text-xs text-slate-400">{e.email}</div>}</td>
                      <td className="py-3 px-4 text-sm">{e.phone}</td>
                      <td className="py-3 px-4 text-sm">{e.property_interest} {e.location_interest && `in ${e.location_interest}`}</td>
                      <td className="py-3 px-4"><span className={`text-xs font-bold px-2 py-1 rounded-full ${e.status === 'new' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{e.status}</span></td>
                      <td className="py-3 px-4 text-sm text-slate-500">{new Date(e.created_at).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {enquiries.length === 0 && <div className="p-8 text-center text-slate-400">No enquiries yet</div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
